import { PreviewCanvasScale } from "./shared/preview-canvas-scale";

const GRID_LINE = "/assets-next/tools/previews/svg/bf-report-grid-line.svg";
const ALL_SPIKES_LINE =
  "/assets-next/tools/previews/svg/bf-report-all-spikes-line.svg";
const NON_HOLIDAY_LINE =
  "/assets-next/tools/previews/svg/bf-report-non-holiday-line.svg";

/** Horizontal grid lines — Figma 11136:41126–41134 at left 30, width 327.946. */
const GRID_LINE_TOPS = [
  71.85, 85.6, 99.35, 113.11, 126.86, 140.61, 154.36, 168.12, 181.87,
] as const;

/** Y-axis labels — Figma 11136:41138–41143, 41135 at left 30. */
const Y_LABELS = [
  { value: 100, top: 62.33 },
  { value: 90, top: 76.08 },
  { value: 80, top: 89.83 },
  { value: 70, top: 103.58 },
  { value: 60, top: 117.34 },
  { value: 50, top: 131.09 },
  { value: 40, top: 144.84 },
  { value: 30, top: 158.6 },
  { value: 20, top: 172.35 },
] as const;

/** X-axis labels — Figma 11136:41145–41159 at top 182.93, centered on left. */
const X_LABELS = [
  { value: "-12", left: 49.74 },
  { value: "-10", left: 74.7 },
  { value: "-8", left: 98.04 },
  { value: "-6", left: 119.75 },
  { value: "-4", left: 142.47 },
  { value: "-2", left: 164.18 },
  { value: "0", left: 185.4 },
  { value: "+2", left: 207 },
  { value: "+4", left: 230.77 },
  { value: "+6", left: 254.04 },
  { value: "+8", left: 277.32 },
  { value: "+10", left: 301.59 },
  { value: "+12", left: 326.98 },
  { value: "+14", left: 352.37 },
] as const;

/**
 * Black Friday report card inset (Figma 11115:59271, 391×291).
 * Scales down on the mobile preview shell (243×181) and renders 1:1 from md+.
 */
export function BfReportCardPreview() {
  return (
    <PreviewCanvasScale testId="bf-report-card-canvas">
      <p className="text-grey-950 absolute top-[29px] left-[29px] text-[12px] leading-[1.4] whitespace-nowrap">
        Index
      </p>

      {/* Legend — Figma 11136:41162 at left 124.5, top 36 */}
      <div className="absolute top-[36px] left-[124.5px] flex items-center gap-[7px]">
        <div className="flex items-center gap-[2px]">
          <span
            aria-hidden
            className="border-grey-600 size-[7px] shrink-0 rounded-[2px] border-[0.5px] border-dashed"
          />
          <span className="text-grey-500 text-[6px] leading-none tracking-[-0.04em] whitespace-nowrap">
            All spikes (n=74, incl. BFCM)
          </span>
        </div>
        <div className="flex items-center gap-[2px]">
          <span
            aria-hidden
            className="border-pulse-700 size-[7px] shrink-0 rounded-[2px] border-[0.75px] border-solid"
          />
          <span className="text-grey-500 text-[6px] leading-none tracking-[-0.04em] whitespace-nowrap">
            Non-holiday spikes only (n=31)
          </span>
        </div>
      </div>

      {/* Grid lines — Figma 11136:41126–41134 SVG strokes */}
      {GRID_LINE_TOPS.map((top) => (
        <div
          key={top}
          aria-hidden
          className="absolute left-[30px] h-0 w-[327.946px]"
          style={{ top }}
        >
          <div className="absolute inset-[-0.4px_0]">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={GRID_LINE}
              alt=""
              className="block size-full max-w-none"
            />
          </div>
        </div>
      ))}

      {/* Y-axis labels */}
      {Y_LABELS.map(({ value, top }) => (
        <span
          key={value}
          className="text-grey-500 absolute left-[30px] text-[6.347px] leading-none tracking-[-0.04em]"
          style={{ top }}
        >
          {value}
        </span>
      ))}

      {/* Non-holiday — solid pulse line (Figma 11136:41160, below dashed line) */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-[71.85px] left-[44.81px] h-[108.963px] w-[312.077px]"
      >
        <div className="absolute inset-[0_0_-0.43%_0]">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={NON_HOLIDAY_LINE}
            alt=""
            className="block size-full max-w-none"
          />
        </div>
      </div>

      {/* All spikes — dashed grey line (Figma 11136:41161, on top) */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-[71.85px] left-[44.81px] h-[110.02px] w-[312.077px]"
      >
        <div className="absolute inset-[0_0_-0.21%_0]">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={ALL_SPIKES_LINE}
            alt=""
            className="block size-full max-w-none"
          />
        </div>
      </div>

      {/* X-axis labels */}
      {X_LABELS.map(({ value, left }) => (
        <span
          key={value}
          className="text-grey-500 absolute -translate-x-1/2 text-center text-[5.818px] leading-none tracking-[-0.04em] whitespace-nowrap"
          style={{ left, top: 182.93 }}
        >
          {value}
        </span>
      ))}

      <p
        className="text-grey-600 absolute -translate-x-1/2 text-center text-[5.818px] leading-none tracking-[-0.04em] whitespace-nowrap"
        style={{ left: 194.2, top: 195.62 }}
      >
        Months from peak
      </p>
    </PreviewCanvasScale>
  );
}
